import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ClipboardCheck, ShieldCheck, Wrench } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { ArticleCard } from "@/components/ArticleCard";
import { ScoreBadge } from "@/components/ScoreBadge";

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [threshold, setThreshold] = useState(70);

  useEffect(() => {
    api.get("/scoring-config").then((r) => { setCategories(r.data.categories); setThreshold(r.data.pass_threshold); });
    api.get("/articles?limit=6").then((r) => setArticles(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <Seo />

      {/* HERO */}
      <section className="border-b-2 border-stone-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="font-mono-pm text-xs tracking-[0.25em] uppercase text-[#991B1B] mb-5">Independent Reviews for Property Managers</p>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight text-stone-900">
              Property Management Technology.<br /><span className="italic">Put to The PM Test.</span>
            </h1>
            <p className="text-lg text-stone-600 mt-6 max-w-xl leading-relaxed">
              Independent reviews and real-world comparisons of the software, technology, and tools property managers rely on every day.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/software" data-testid="hero-cta-reviews"
                className="inline-flex items-center gap-2 bg-stone-900 text-white px-8 py-4 font-mono-pm font-bold text-sm tracking-widest uppercase hover:-translate-y-1 hover:hard-shadow-red transition-transform duration-200">
                Explore Reviews <ArrowRight size={16} />
              </Link>
              <Link to="/share-your-stack" className="font-mono-pm text-sm tracking-widest uppercase text-stone-900 underline underline-offset-4 hover:text-[#991B1B]">
                Share Your Stack
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <div className="relative">
              <ScoreBadge score={88} passes={true} size="lg" />
              <div className="absolute -top-5 -right-5 bg-white border-2 border-stone-900 px-3 py-2 hard-shadow">
                <p className="font-mono-pm text-[10px] tracking-widest uppercase text-stone-500">Sample Score</p>
                <p className="font-mono-pm text-xs font-bold text-stone-900">Out of 100</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* METHODOLOGY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16 md:py-24">
        <div className="max-w-3xl">
          <p className="font-mono-pm text-xs tracking-[0.25em] uppercase text-[#991B1B] mb-4">The PM Test Methodology</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl font-bold leading-tight text-stone-900">
            Nine categories. One score out of 100.
          </h2>
          <p className="text-lg text-stone-600 mt-4 leading-relaxed">
            Most reviews tell you what features a product has. The PM Test asks whether those features actually work in real-world property operations. Every product is rated 0–10 across nine categories.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-0 border-t border-l border-stone-900">
          {categories.map((c, i) => (
            <div key={c.key} className="border-r border-b border-stone-900 p-6 bg-white flex items-start gap-4">
              <span className="font-mono-pm text-2xl font-black text-stone-300">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h3 className="font-mono-pm text-sm font-bold uppercase tracking-wide text-stone-900 leading-tight">{c.label}</h3>
                <p className="font-mono-pm text-xs text-stone-500 mt-2">Scored 0–10</p>
              </div>
            </div>
          ))}
        </div>
        <p className="font-mono-pm text-xs text-stone-500 mt-4">
          A product must score {threshold}/100 or higher to earn a "Passes The PM Test" verdict.
        </p>
      </section>

      {/* WHY */}
      <section className="border-y-2 border-stone-900 bg-stone-900 text-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            { icon: Wrench, title: "Operator-Led", text: "Reviewed by property managers with real maintenance, vendor and tenant experience — not marketers." },
            { icon: ClipboardCheck, title: "Standardized Scoring", text: "The same nine-category rubric applied to every product, so comparisons are fair and consistent." },
            { icon: ShieldCheck, title: "Independent", text: "Affiliate relationships never influence our scores or recommendations. Ever." },
          ].map((f) => (
            <div key={f.title}>
              <f.icon size={28} className="text-[#CCFF00]" />
              <h3 className="font-serif-display text-2xl font-bold mt-4">{f.title}</h3>
              <p className="text-stone-400 mt-2 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* LATEST */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-16 md:py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="font-mono-pm text-xs tracking-[0.25em] uppercase text-[#991B1B] mb-3">Latest</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-bold text-stone-900">Published Reviews & Guides</h2>
          </div>
        </div>
        {articles.length === 0 ? (
          <div className="border-2 border-dashed border-stone-300 bg-white p-12 text-center">
            <p className="font-serif-display text-2xl text-stone-900">Reviews publishing soon.</p>
            <p className="font-mono-pm text-sm text-stone-500 mt-3 max-w-lg mx-auto">
              Our first editorial reviews are in progress. We never publish scores or verdicts until a real, hands-on review is complete.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((a) => <ArticleCard key={a.slug} article={a} />)}
          </div>
        )}
      </section>
    </div>
  );
}
